import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * The server-rendered empty state captured at connect, restored on reset so the
   * Popular searches / Bestsellers / Collections (which come from the Header section
   * settings) survive a reset without refetching a section that can't read them.
   * @type {HTMLElement | null}
   */
  #cachedEmptyState = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    // Capture the server-rendered empty state (painted from the Header section
    // settings) so #resetSearch can restore it without re-fetching the standalone
    // `predictive-search-empty` section, which renders in isolation and can't read
    // those settings.
    this.#cachedEmptyState = this.refs.predictiveSearchResults?.cloneNode(true) ?? null;

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }

    // Move the cursor straight into the search field every time the modal opens
    // so the user can start typing without clicking. `preventScroll` keeps the
    // page from jumping during the panel's open animation.
    this.refs.searchInput?.focus({ preventScroll: true });
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
        '.predictive-search-results__wrapper-products, ' +
        '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch + compose the predictive results. HYBRID: the PRODUCTS section comes from
   * Meilisearch (ranking parity with /search + the collection grid; the index is
   * products-only), while blog/collections/pages stay on Shopify's native predictive
   * API. Both are fetched in parallel and composed into a single morph (no flicker).
   * If Meili is unreachable the native products (rendered by sections/predictive-search.liquid)
   * are left in place as a graceful fallback.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'product,page,collection,article');
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();

    let nativeMarkup = null;
    let meiliHits = null;
    let meiliFaqHits = null;
    try {
      [nativeMarkup, meiliHits, meiliFaqHits] = await Promise.all([
        sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url),
        // null on missing config / network error → native products stay as fallback.
        this.#fetchMeiliProducts(searchTerm).catch(() => null),
        // Real FAQ Q&A from the aico FAQ index; null → native pages stay as fallback.
        this.#fetchMeiliFaqs(searchTerm).catch(() => null),
      ]);
    } catch (error) {
      if (abortController.signal.aborted) return;
      throw error;
    }

    if (abortController.signal.aborted || !nativeMarkup) return;

    morph(predictiveSearchResults, this.#composeResults(nativeMarkup, meiliHits, meiliFaqHits, searchTerm));
    this.#renderNoResultsSuggestions();
    this.#resetScrollPositions();
  }

  /** @returns {any} Meili config emitted by snippets/search-modal.liquid (header), or null. */
  get #meiliConfig() {
    return /** @type {any} */ (window).MeilisearchPredictiveConfig ?? null;
  }

  /**
   * Query Meilisearch for products. Returns the hits array, or null when Meili is
   * not configured or the request fails (→ native products stay as the fallback).
   * @param {string} term
   * @returns {Promise<Array|null>}
   */
  async #fetchMeiliProducts(term) {
    const c = this.#meiliConfig;
    if (!c || !c.meilisearchHost || !c.meilisearchApiKey || !c.meilisearchIndex) return null;
    // Variant-split docs (one per option-value group, see meilisearch-core.js) stay
    // separate suggestions, matching the /search page; #meiliProductTile deep-links them.
    const endpoint = `${c.meilisearchHost}/indexes/${c.meilisearchIndex}/search`;
    const headers = { Authorization: `Bearer ${c.meilisearchApiKey}`, 'Content-Type': 'application/json' };
    // hitsPerPage/page (not limit) makes Meili count exhaustively and return
    // `totalHits`, the same number the /search page shows via instantsearch's
    // nbHits. `limit` only yields estimatedTotalHits, and the heading used to
    // print hits.length, i.e. the 6 tiles rendered, not the total.
    const perPage = c.productLimit || 6;
    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({ q: term, hitsPerPage: perPage, page: 1 }),
    });
    if (!response.ok) throw new Error(`Meilisearch ${response.status}`);
    const data = await response.json();
    const hits = Array.isArray(data.hits) ? data.hits : [];
    hits.totalHits = typeof data.totalHits === 'number' ? data.totalHits : hits.length;
    return hits;
  }

  /**
   * Query the aico Meilisearch FAQ index (faqs_<domain>) for real Q&A. Returns the
   * hits array, or null when not configured / the request fails (→ native pages
   * stay as the fallback FAQ section).
   * @param {string} term
   * @returns {Promise<Array|null>}
   */
  async #fetchMeiliFaqs(term) {
    const c = this.#meiliConfig;
    if (!c || !c.meilisearchHost || !c.meilisearchApiKey || !c.faqIndex) return null;
    const limit = c.faqLimit || 4;
    const response = await fetch(`${c.meilisearchHost}/indexes/${c.faqIndex}/search`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${c.meilisearchApiKey}`, 'Content-Type': 'application/json' },
      // The FAQ index holds one doc per (FAQ, PRODUCT) pair, so a Q&A picked for
      // several products comes back once per product (e.g. "foam" returned the
      // same toothpaste question twice). Unlike products there is no shared id to
      // `distinct` on server-side (faqId is per pair, not per question), so
      // over-fetch and collapse by question text below — without the over-fetch
      // the de-dupe would eat rows out of the visible list.
      body: JSON.stringify({ q: term, limit: limit * 5 }),
    });
    if (!response.ok) throw new Error(`Meilisearch FAQ ${response.status}`);
    const data = await response.json();
    return this.#dedupeFaqHits(Array.isArray(data.hits) ? data.hits : []).slice(0, limit);
  }

  /**
   * Collapse FAQ hits that render the same question in the current language —
   * the same Q&A attached to several products — keeping the best-ranked one.
   * @param {Array} hits
   * @returns {Array}
   */
  #dedupeFaqHits(hits) {
    const seen = new Set();
    return hits.filter((hit) => {
      const key = this.#psStripHtml(this.#meiliField(hit, 'question')).toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  /**
   * Overlay Meili product hits onto the native predictive markup, drop the (now
   * stale) single-result shortcut, recompute the no-results state, and return the
   * composed HTML string. When meiliHits is null (Meili down) the native markup is
   * returned untouched.
   * @param {string} nativeMarkup
   * @param {Array|null} meiliHits
   * @param {string} term
   * @returns {string}
   */
  #composeResults(nativeMarkup, meiliHits, meiliFaqHits, term) {
    const tmp = document.createElement('div');
    tmp.innerHTML = nativeMarkup;
    const inner = tmp.querySelector('.ps-results__inner');
    if (!inner) return nativeMarkup;

    if (meiliHits === null) return tmp.innerHTML;

    const existing = inner.querySelector('.ps-results__section--products');
    if (meiliHits.length > 0) {
      const sectionHTML = this.#meiliProductsSection(meiliHits, term);
      if (existing) existing.outerHTML = sectionHTML;
      else inner.insertAdjacentHTML('afterbegin', sectionHTML);
    } else if (existing) {
      existing.remove();
    }

    // Overlay real FAQ Q&A onto the native FAQ section (data-predictive-faq),
    // replacing the Shopify-Pages fallback. null → leave native pages in place.
    if (meiliFaqHits !== null) {
      const existingFaq = inner.querySelector('[data-predictive-faq]');
      if (meiliFaqHits.length > 0) {
        const faqHTML = this.#meiliFaqSection(meiliFaqHits, term);
        if (existingFaq) existingFaq.outerHTML = faqHTML;
        else inner.insertAdjacentHTML('beforeend', faqHTML);
      } else if (existingFaq) {
        existingFaq.remove();
      }
    }

    // The native single-result shortcut counted native products, so it can be stale
    // once Meili is in play — drop it; Enter then falls back to the full /search page.
    inner.querySelector('[data-single-result-url]')?.remove();

    // Recompute no-results from the overlaid content: any content section present →
    // drop the no-results state; none → ensure it (covers native-loose-products vs Meili-0).
    const contentSections = inner.querySelectorAll('.ps-results__section:not(.ps-results__no-results-state)');
    const noResults = inner.querySelector('.ps-results__no-results-state');
    if (contentSections.length > 0) {
      noResults?.remove();
    } else if (!noResults) {
      inner.insertAdjacentHTML('beforeend', this.#meiliNoResultsState(term));
    }

    // Keep the a11y live region honest if we flipped to the no-results state.
    if (inner.querySelector('.ps-results__no-results-state')) {
      const live = inner.querySelector('[role="status"]');
      if (live) live.textContent = this.#meiliNoResultsHeading(term);
    }

    return tmp.innerHTML;
  }

  // ── Meili product-tile builders (mirror sections/predictive-search.liquid) ─────
  #psEscape(text) {
    const d = document.createElement('div');
    d.textContent = text == null ? '' : String(text);
    return d.innerHTML;
  }

  #psImageUrl(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') !== -1 ? '&' : '?') + 'width=' + width;
  }

  /**
   * Index documents carry per-language fields suffixed with the two-letter
   * language code (`question_de`, `title_fr`, ...). `currentLocale` is
   * `request.locale.iso_code`, which can be regional (`de-CH`), so normalize to
   * the language before looking the field up - otherwise the lookup misses and
   * silently falls back to the primary language.
   */
  #meiliLocale() {
    const locale = (this.#meiliConfig && this.#meiliConfig.currentLocale) || 'de';
    return String(locale).toLowerCase().slice(0, 2);
  }

  #meiliField(hit, base) {
    return hit[`${base}_${this.#meiliLocale()}`] || hit[base] || '';
  }

  #meiliProductTile(hit) {
    const handle = this.#meiliField(hit, 'handle');
    const root = window.Shopify?.routes?.root || '/';
    // Split doc (see meilisearch-core.js splitLabel/splitVariant): label the group and
    // open the PDP on its first available variant
    const sv = hit.splitValues && typeof hit.splitValues === 'object' ? hit.splitValues : null;
    const splitLabel = sv
      ? Object.values(sv)
          .filter((v) => v != null && String(v).trim())
          .map((v) => String(v).trim())
          .join(' / ')
      : '';
    const baseTitle = this.#meiliField(hit, 'title');
    const title = splitLabel ? `${baseTitle} – ${splitLabel}` : baseTitle;
    let url = `${root}products/${handle}`;
    if (splitLabel) {
      const variants = Array.isArray(hit.variants) ? hit.variants : [];
      // Same online-stock rule as meilisearch-core.js isVariantInStock
      const inStock = (v) =>
        v.inventoryPolicy === 'CONTINUE' ||
        v.inventoryTracked === false ||
        (typeof v.sellableOnlineQuantity === 'number' ? v.sellableOnlineQuantity > 0 : !!v.availableForSale);
      const variant = variants.find((v) => v && inStock(v)) || variants[0];
      if (variant && variant.id) url += `?variant=${encodeURIComponent(String(variant.id))}`;
    }
    const img = Array.isArray(hit.images) && hit.images[0] ? hit.images[0].src || hit.images[0].url || '' : '';
    const media = img
      ? `<img class="ps-results__tile-image" src="${this.#psEscape(this.#psImageUrl(img, 400))}" srcset="${this.#psEscape(
        this.#psImageUrl(img, 188)
      )} 188w, ${this.#psEscape(this.#psImageUrl(img, 232))} 232w, ${this.#psEscape(
        this.#psImageUrl(img, 300)
      )} 300w, ${this.#psEscape(this.#psImageUrl(img, 400))} 400w" sizes="(min-width: 750px) 190px, 45vw" loading="lazy" alt="${this.#psEscape(
        title
      )}">`
      : `<span class="ps-results__tile-image ps-results__tile-image--placeholder">${this.#psEscape(title)}</span>`;
    return `<li class="ps-results__grid-item"><a class="ps-results__tile" href="${this.#psEscape(
      url
    )}" aria-label="${this.#psEscape(title)}">${media}</a></li>`;
  }

  #meiliProductsSection(hits, term) {
    const c = this.#meiliConfig || {};
    const root = window.Shopify?.routes?.root || '/';
    const tiles = hits
      .slice(0, c.productLimit || 6)
      .map((hit) => this.#meiliProductTile(hit))
      .join('');
    // &tab=products preselects the Products tab on /search (search-tabs.js reads ?tab),
    // matching the server-rendered products "See all" (sections/predictive-search.liquid)
    // and the blog/FAQ "See all" links. Without it the Meili-overlaid link landed on 'All'.
    const seeAllHref = `${c.searchUrl || `${root}search`}?q=${encodeURIComponent(term)}&options[prefix]=last&tab=products`;
    return (
      '<section class="ps-results__section ps-results__section--products">' +
      `<h3 class="ps-results__title">${this.#psEscape(c.productsTitle || 'Products')} (${hits.totalHits ?? hits.length})</h3>` +
      '<div class="ps-results__products">' +
      `<ul class="ps-results__grid list-unstyled" role="list">${tiles}</ul>` +
      '<div class="ps-results__see-all-wrap">' +
      `<a class="cta cta--button-secondary cta--size-small" href="${this.#psEscape(
        seeAllHref
      )}"><span class="cta__label">${this.#psEscape(c.seeAllLabel || 'See all')}</span></a>` +
      '</div>' +
      '</div>' +
      '</section>'
    );
  }

  /** Strip HTML/rich-text from a FAQ field down to plain text for the compact row. */
  #psStripHtml(html) {
    const d = document.createElement('div');
    d.innerHTML = html == null ? '' : String(html);
    return (d.textContent || '').replace(/\s+/g, ' ').trim();
  }

  /**
   * Build the predictive FAQ section from Meili FAQ hits: each question links to
   * the results page FAQ tab (which shows the answer excerpt). Mirrors the native
   * pages section markup so styling is unchanged.
   */
  #meiliFaqSection(hits, term) {
    const c = this.#meiliConfig || {};
    const root = window.Shopify?.routes?.root || '/';
    const searchUrl = c.searchUrl || `${root}search`;
    const rows = hits
      .slice(0, c.faqLimit || 4)
      .map((hit) => {
        const q = this.#psStripHtml(this.#meiliField(hit, 'question'));
        if (!q) return '';
        const href = `${searchUrl}?q=${encodeURIComponent(q)}&tab=faq`;
        return `<li><a class="ps-results__link" href="${this.#psEscape(href)}">${this.#psEscape(q)}</a></li>`;
      })
      .join('');
    const seeAllHref = `${searchUrl}?q=${encodeURIComponent(term)}&tab=faq`;
    return (
      '<section class="ps-results__section ps-results__section--links ps-results__section--faq" data-predictive-faq>' +
      `<h3 class="ps-results__title ps-results__title--sm">${this.#psEscape(c.faqTitle || 'Frequently asked questions')}</h3>` +
      `<ul class="ps-results__links list-unstyled" role="list">${rows}</ul>` +
      `<a class="ps-results__see-all-link" href="${this.#psEscape(seeAllHref)}">${this.#psEscape(c.seeAllLabel || 'See all')}</a>` +
      '</section>'
    );
  }

  #meiliNoResultsHeading(term) {
    let tpl = (this.#meiliConfig && this.#meiliConfig.noResultsTitleTemplate) || 'No results for "%%TERMS%%".';
    // Shopify's `t` filter HTML-escapes its output; decode entities (e.g. &quot;)
    // so the heading reads correctly once placed via textContent / escaped innerHTML.
    const ta = document.createElement('textarea');
    ta.innerHTML = tpl;
    tpl = ta.value;
    return tpl.split('%%TERMS%%').join(term);
  }

  #meiliNoResultsState(term) {
    return (
      '<section class="ps-results__section ps-results__no-results-state" data-no-results>' +
      `<p class="ps-results__no-results-heading predictive-search-results__no-results">${this.#psEscape(
        this.#meiliNoResultsHeading(term)
      )}</p>` +
      '<div class="ps-results__no-results-suggestions"></div>' +
      '</section>'
    );
  }

  /**
   * Enrich the zero-results view with the same Popular searches / Bestsellers /
   * Collections the empty state shows (Figma 786:17004 / 786:19306). The results
   * section renders in isolation and can't read the Header section settings, so we
   * graft in the empty state captured at connect — mirroring the #resetSearch path.
   * No-op unless the fetched markup is the no-results state (`[data-no-results]`).
   */
  #renderNoResultsSuggestions() {
    const target = this.refs.predictiveSearchResults.querySelector(
      '[data-no-results] .ps-results__no-results-suggestions'
    );
    if (!target || target.childElementCount > 0 || !this.#cachedEmptyState) return;

    for (const section of this.#cachedEmptyState.querySelectorAll('.search-modal__section')) {
      target.appendChild(section.cloneNode(true));
    }
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */(id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
    this.#setFilledState(false);
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
    this.#setFilledState(true);
  }

  /**
   * Toggle the search field's "filled" state. The `.field` component (base.css)
   * already drives this CSS-only via `:has(.field__input:not(:placeholder-shown))`;
   * this mirrors it with the component's complete `is-focus` state class as a
   * belt-and-suspenders fallback for engines that lag on re-evaluating `:has()`
   * after a value change. No-op when the input isn't inside a `.field`.
   * @param {boolean} filled - Whether the input currently holds a query.
   */
  #setFilledState(filled) {
    const field = this.refs.searchInput?.closest('.field');
    field?.classList.toggle('is-focus', filled);
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = () => {
    const { predictiveSearchResults, searchInput } = this.refs;

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    // Restore the empty state captured at connect (Popular searches / Bestsellers /
    // Collections from the Header section settings). We intentionally do NOT re-fetch
    // the standalone `predictive-search-empty` section here: it renders in isolation
    // and can't read the header section's settings, so it would blank the lists. The
    // empty state is static, so restoring the cached copy is equivalent and cheaper.
    // Recently-viewed injection is dropped on purpose — the Curaprox design has no
    // recently-viewed row (see snippets/predictive-search-empty-state.liquid).
    if (this.#cachedEmptyState) {
      morph(predictiveSearchResults, this.#cachedEmptyState.cloneNode(true));
    }

    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
